import AdminLayout from "../../commponents/layout/adminLayout";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye } from "lucide-react";
import type { TableColumn } from "../../commponents/table/types";
import DataTable from "../../commponents/table/DataTable";
import StatusFilter from "../../commponents/filter/StatusFilter";
import DosStatusBadge from "../../commponents/dos/DosStatusBadge";
import { getDosList, getDosStatus } from "../../api/dosApi";
import { getAuthUser } from "../../api/authApi";
import { ROLE, isSalesRole } from "../../lib/roleAccess";
import { getApiErrorMessage } from "../../lib/httpError";
import { formatDateOnly, formatDateTime } from "../../helpers/dateFormat";
import type { DosListItem, DosStatus } from "../../type/dos";

const PENDING_APPROVAL = "PENDING_APPROVAL";

export default function DosList() {
  const role = getAuthUser()?.role;
  const isSales = isSalesRole(role);
  const isAdmin = role === ROLE.ADMIN;

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [dosList, setDosList] = useState<DosListItem[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [statusOptions, setStatusOptions] = useState<DosStatus[]>([]);

  const navigate = useNavigate();

  const columns: TableColumn<DosListItem>[] = [
    { key: "dos_no", title: "DOS No" },
    {
      key: "customer",
      title: "Customer",
      render: (row) => row.customer?.company_name || row.customer?.customer_name || "-",
    },
    { key: "po_number", title: "PO Number" },
    {
      key: "delivery_date",
      title: "Delivery Date",
      render: (row) => formatDateOnly(row.delivery_date),
    },
    {
      key: "status",
      title: "Status",
      render: (row) => <DosStatusBadge status={row.status} />,
    },
    // Sales hanya melihat DOS miliknya, jadi kolom Sales hanya untuk Admin/Staff.
    ...(isSales
      ? []
      : [
          {
            key: "sales",
            title: "Sales",
            render: (row: DosListItem) => row.sales?.name ?? "-",
          } as TableColumn<DosListItem>,
        ]),
    {
      key: "createdAt",
      title: "Created",
      render: (row) => formatDateTime(row.createdAt),
    },
    {
      key: "id",
      title: "Action",
      render: (row) => (
        <button
          onClick={() => navigate(`/dos/${row.id}`)}
          aria-label="Detail"
          className="rounded-lg bg-blue-100 p-2 text-blue-600 hover:bg-blue-200">
          <Eye size={18} />
        </button>
      ),
    },
  ];

  const fetchDos = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const response = await getDosList({
        page: currentPage,
        limit: pageSize,
        search,
        status,
      });

      setDosList(response.data.dosList ?? []);
      // Daftar kosong -> backend mengirim totalPages 0
      setTotalPages(Math.max(1, response.data.pagination?.totalPages ?? 1));
    } catch (error) {
      setDosList([]);
      setErrorMessage(await getApiErrorMessage(error, "Failed to load DOS data."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDos();
  }, [currentPage, pageSize, search, status]);

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const response = await getDosStatus();
        setStatusOptions(response.data.dosStatusList ?? []);
      } catch {
        // Filter status tetap bisa dikosongkan; error daftar ditangani fetchDos.
        setStatusOptions([]);
      }
    };

    loadStatus();
  }, []);

  const changeStatus = (value: string) => {
    setStatus(value);
    setCurrentPage(1);
  };

  return (
    <AdminLayout>
      <div className="rounded-3xl bg-white p-6">
        {isAdmin && (
          <div className="mb-4 flex justify-end">
            <button
              onClick={() => changeStatus(status === PENDING_APPROVAL ? "" : PENDING_APPROVAL)}
              aria-pressed={status === PENDING_APPROVAL}
              className={`rounded-xl border px-4 py-2 text-sm font-medium ${
                status === PENDING_APPROVAL
                  ? "border-yellow-500 bg-yellow-100 text-yellow-900"
                  : "border-gray-200 text-gray-700 hover:bg-gray-50"
              }`}>
              Pending Approval
            </button>
          </div>
        )}

        {loading && <p className="mb-3 text-sm text-gray-400">Loading...</p>}

        {errorMessage && (
          <div
            role="alert"
            className="mb-4 flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <span>{errorMessage}</span>
            <button
              onClick={fetchDos}
              className="rounded-lg border border-red-300 px-3 py-1 font-medium hover:bg-red-100">
              Retry
            </button>
          </div>
        )}

        <DataTable
          title="DOS List"
          columns={columns}
          data={dosList}
          showImport={false}
          showAdd={isSales}
          onAdd={() => navigate("/dos/add")}
          searchPlaceholder="Search DOS No or PO Number"
          filterContent={
            <StatusFilter
              value={status}
              onChange={changeStatus}
              options={statusOptions.map((item) => ({
                label: item.name,
                value: item.code,
              }))}
            />
          }
          onSearch={(value) => {
            setSearch(value);
            setCurrentPage(1);
          }}
          pagination={{
            currentPage,
            totalPages,
            pageSize,
            onPageChange: setCurrentPage,
            onPageSizeChange: (size) => {
              setPageSize(size);
              setCurrentPage(1);
            },
          }}
        />
      </div>
    </AdminLayout>
  );
}
